import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { JobService, type JobsFilters } from '../api/job-service';
import { JOB_POLL_INTERVAL_MS } from '../magic-values';
import type { PaginationDto } from '../types/common';
import { JobStatus, type Job, type ListJobsResponse } from '../types/job';

const ACTIVE_JOB_STATUSES = [JobStatus.PENDING, JobStatus.RUNNING];

export function isJobActive(status: JobStatus | undefined): boolean {
  return status !== undefined && ACTIVE_JOB_STATUSES.includes(status);
}

export const jobQueryKeys = {
  all: ['jobs'] as const,
  list: (filters: JobsFilters, pagination: PaginationDto = {}) =>
    ['jobs', 'list', filters, pagination] as const,
  byId: (id: string | null) => ['jobs', id] as const,
};

export function useJobs(
  filters: JobsFilters,
  pagination?: PaginationDto,
): UseQueryResult<ListJobsResponse> {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: jobQueryKeys.list(filters, pagination),
    queryFn: async () => {
      const result = await JobService.getJobs(filters, pagination);
      for (const job of result.items) {
        queryClient.setQueryData(jobQueryKeys.byId(job._id), job);
      }
      return result;
    },
    refetchInterval: (query) =>
      query.state.data?.items.some((job) => isJobActive(job.status)) ? JOB_POLL_INTERVAL_MS : false,
  });
}

export function useJob(id: string | null, accountId: string | null): UseQueryResult<Job> {
  return useQuery({
    queryKey: jobQueryKeys.byId(id),
    queryFn: () => JobService.getJob(id as string, accountId as string),
    enabled: Boolean(id) && Boolean(accountId),
    refetchInterval: (query) =>
      isJobActive(query.state.data?.status) ? JOB_POLL_INTERVAL_MS : false,
  });
}
